import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ScanRegulatoryReportPage from '../../ScanRegulatoryReportPage';
import { showToastError } from '../../components/ToastNotifications';
import { getScanRegulatoryReport, type ScanRegulatoryReportRow } from '../../hooks/useScanRegulatoryReport';
import type { SessionUser } from '../../types';
import { getErrorMessage } from '../../utils/requestErrors';

export default function ScanRegulatoryReportContainer({ currentUser }: { currentUser?: SessionUser | null }) {
  const navigate = useNavigate();
  const allowed = currentUser?.isSuperUser === true ||
    currentUser?.internalRoleActions?.includes('ReadInternalReports') === true;
  const [qr, setQr] = useState('');
  const [scanning, setScanning] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rows, setRows] = useState<ScanRegulatoryReportRow[]>([]);
  const requestRef = useRef<AbortController | null>(null);

  useEffect(() => {
    setScanning(false);
    setRows([]);
    setError('');
    setLoading(false);
    return () => {
      requestRef.current?.abort();
      requestRef.current = null;
    };
  }, [allowed, currentUser?.id]);

  const lookup = useCallback(async (value: string) => {
    if (!allowed) return;
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    setScanning(false);
    setQr(value);
    setRows([]);
    setError('');
    if (!value.trim()) {
      setLoading(false);
      setError('Enter a Retail ID QR URL or short code.');
      return;
    }
    setLoading(true);
    try {
      const result = await getScanRegulatoryReport(value.trim(), controller.signal);
      if (requestRef.current === controller && !controller.signal.aborted) setRows(result);
    } catch (failure) {
      if (requestRef.current !== controller || controller.signal.aborted) return;
      const message = getErrorMessage(failure, 'Could not retrieve the Scan Regulatory Report.');
      setError(message);
      showToastError(message);
    } finally {
      if (requestRef.current === controller) setLoading(false);
    }
  }, [allowed]);

  const onScan = useCallback((value: string) => { void lookup(value); }, [lookup]);
  const onCameraError = useCallback((message: string) => {
    setScanning(false);
    setError(message);
    showToastError(message);
  }, []);

  return <ScanRegulatoryReportPage
    allowed={allowed} qr={qr} scanning={scanning} loading={loading} error={error} rows={rows}
    onQrChange={setQr} onLookup={() => { void lookup(qr); }} onScan={onScan} onCameraError={onCameraError}
    onToggleCamera={() => { setError(''); setScanning(active => !active); }}
    onBack={() => navigate('/internal/reports')}
  />;
}
